'use client';

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useGlobal } from '@/contexts/GlobalContext';
import { useToast } from '@/contexts/ToastContext';
import { useConfirmModal } from '@/contexts/ConfirmModalContext';
import ConfirmModalContent from '@/components/layouts/ConfirmModalContent';
import Button from '@/components/common/Button';
import { Status } from '@/types/teams';
import { inviteTeam, cancelInvitation } from '@/repositories/teams';

import { mutateWithoutLoading } from '@/hooks/swr/useSWRWithLoadingControl';

interface TeamInvitationButtonProps {
  propsTeamUUID: string;
  propsFootballerId: number;
  propsTeamMemberStatus: Status | undefined;
}

/**
 * チーム招待ボタンコンポーネント
 * @param param0
 * @returns
 */

const TeamInvitationButton: React.FC<TeamInvitationButtonProps> = ({
  propsTeamUUID,
  propsFootballerId,
  propsTeamMemberStatus
}) => {
  /**
   * context & hooks
   */
  const navigate = useNavigate();
  const location = useLocation();
  const context = useGlobal();
  const { showSuccessMessage, showErrorMessage } = useToast();
  const { openConfirmModal, closeConfirmModal } = useConfirmModal();

  /**
   * useState
   */
  const [isLoading, setIsLoading] = useState(false);
  const [supportingButton, setSupportingButton] = useState<React.ReactNode>(null);

  /**
   * チームへ招待をリクエスト
   */
  const handleInviteTeam = async (footballerId: number) => {
    try {
      // ボタンローディング表示
      setIsLoading(true);

      // チーム招待リクエスト
      const res = await inviteTeam(propsTeamUUID, footballerId, context.csrftoken || '');
      if (!res.ok) {
        throw new Error(res.detail);
      }

      // 1秒の遅延を追加
      await new Promise((resolve) => setTimeout(resolve, 500));

      // NOTE: mutateとnavigate(replace:true)の両方を実行し、情報を最新化
      mutateWithoutLoading();
      // NOTE:更新後の画面遷移で、以前の状態がキャッシュされたまま画面表示されてしまうのを防止する
      navigate(location.pathname, { replace: true, state: { skipLoading: true } });

      // トースト表示
      showSuccessMessage('チーム招待しました。');
    } catch (err) {
      // エラートースト表示
      showErrorMessage('データ通信に失敗しました。しばらくしてから再度お試しください。');
      throw err;
    } finally {
      // ボタンローディング非表示
      setIsLoading(false);
    }
  };

  /**
   * チーム招待の取り消し
   */
  const handleCancelInvitation = async (footballerId: number) => {
    // 確認モーダル閉じる（このタイミングが重要）
    closeConfirmModal();

    try {
      // ボタンローディング表示
      setIsLoading(true);

      // チーム招待の取り消し
      const res = await cancelInvitation(propsTeamUUID as string, footballerId, context.csrftoken || '');
      if (!res.ok) {
        throw new Error(res.detail);
      }

      // 1秒の遅延を追加
      await new Promise((resolve) => setTimeout(resolve, 500));

      // NOTE: mutateとnavigate(replace:true)の両方を実行し、情報を最新化
      mutateWithoutLoading();
      // NOTE:更新後の画面遷移で、以前の状態がキャッシュされたまま画面表示されてしまうのを防止する
      navigate(location.pathname, { replace: true, state: { skipLoading: true } });

      // トースト表示
      showSuccessMessage('招待を取消しました。');
    } catch (err) {
      // エラートースト表示
      showErrorMessage('データ通信に失敗しました。しばらくしてから再度お試しください。');
      throw err;
    } finally {
      // ボタンローディング非表示
      setIsLoading(false);
    }
  };

  /**
   * 確認モーダル表示
   */
  const handleConfirmCancelInvitation = (footballerId: number) => {
    openConfirmModal(
      <ConfirmModalContent onClickOKCallback={() => handleCancelInvitation(footballerId)}>
        招待を取り消します。
        <br />
        よろしいですか？
      </ConfirmModalContent>
    );
  };

  useEffect(() => {
    let buttonNode: React.ReactNode;
    switch (propsTeamMemberStatus) {
      case undefined:
      case Status.Deactivated:
        buttonNode = (
          <Button
            type="button"
            buttonType="outlineWhite"
            className="text-xs"
            isLoading={isLoading}
            loadingMessage="チーム招待"
            onClick={() => handleInviteTeam(propsFootballerId)}>
            <svg
              className="w-4 h-4 me-2"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 18">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 8h6m-3 3V5m-6-.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM5 11h3a4 4 0 0 1 4 4v2H1v-2a4 4 0 0 1 4-4Z"
              />
            </svg>
            チーム招待
          </Button>
        );
        break;
      case Status.Requested:
        buttonNode = (
          <Button
            type="button"
            buttonType="outlineWhite"
            className="text-xs"
            isLoading={isLoading}
            loadingMessage="招待中"
            onClick={() => handleConfirmCancelInvitation(propsFootballerId)}>
            <svg
              className="w-4 h-4 me-2"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 18">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M14 3a3 3 0 1 1-1.614 5.53M15 12a4 4 0 0 1 4 4v1h-3.348M10 4.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM5 11h3a4 4 0 0 1 4 4v2H1v-2a4 4 0 0 1 4-4Z"
              />
            </svg>
            招待中
          </Button>
        );
        break;
      case Status.Approved:
        buttonNode = (
          <Button type="button" className="text-xs" buttonType="outlineWhite" loadingMessage="チームメンバー" disabled>
            <svg
              className="w-4 h-4 me-2"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 18">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M14 3a3 3 0 1 1-1.614 5.53M15 12a4 4 0 0 1 4 4v1h-3.348M10 4.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM5 11h3a4 4 0 0 1 4 4v2H1v-2a4 4 0 0 1 4-4Z"
              />
            </svg>
            チームメンバー
          </Button>
        );
        break;
      case Status.OBOG:
        buttonNode = (
          <Button type="button" className="text-xs" buttonType="outlineWhite" loadingMessage="OBOGメンバー" disabled>
            <svg
              className="w-4 h-4 me-2"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 18">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M14 3a3 3 0 1 1-1.614 5.53M15 12a4 4 0 0 1 4 4v1h-3.348M10 4.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM5 11h3a4 4 0 0 1 4 4v2H1v-2a4 4 0 0 1 4-4Z"
              />
            </svg>
            OBOGメンバー
          </Button>
        );
        break;
      default:
        buttonNode = <></>;
        break;
    }
    setSupportingButton(buttonNode);
  }, [propsTeamMemberStatus, isLoading, propsFootballerId]);

  return (
    <>
      <div>{supportingButton}</div>
    </>
  );
};

export default TeamInvitationButton;
